import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Radio, Space, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { saveInvoiceComplaint } from '../api/invoiceWorkflowApi';
import type { InvoiceCoverageLine } from '../../../types/invoiceCoverage';
import type { WorkflowInvoice, WorkflowInvoiceDirection } from '../../../types/workflowInvoice';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { formatCoverageQuantity } from '../invoiceCoverageLabels';
import ProductNameWithDimensions from '../../../components/products/ProductNameWithDimensions';
import { useFeedback } from '../../../utils/feedback';
import { formatUnit } from '../../../utils/units';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import { roundWarehouseQuantity } from '../../../utils/warehouseQuantity';

type Reason = 'ORDINARY' | 'COMPLAINT';

interface InvoiceComplaintEditorProps {
  direction: WorkflowInvoiceDirection;
  /** The correction draft (or approved correction). */
  invoice: WorkflowInvoice;
  /** Lines of the ZK/ZD (names, units); null = not loaded. */
  lines: InvoiceCoverageLine[] | null;
  /** Draft that the viewer may change (backend `canCorrect`). */
  editable: boolean;
  /** Unsaved invoice edits: save them first (the complaint bumps the version). */
  locked?: boolean;
  onSaved: () => void;
}

/** „Płyta dębowa 10 szt.” per complained line (names from the order lines). */
export function formatComplaintLines(
  complaintLines: Array<{ sourceLineId: string; quantity: number }>,
  lines: InvoiceCoverageLine[] | null,
): string {
  return complaintLines.map((row) => {
    const line = lines?.find((candidate) => candidate.id === row.sourceLineId);
    return `${line?.name || '—'} ${formatCoverageQuantity(row.quantity, line?.unit ?? null)}`;
  }).join(', ');
}

/**
 * WP15 (docs/decisions/2026-09-25-wp15-reklamacje.md): „Powód korekty” on a
 * correction draft — an ordinary correction or „Reklamacja” with the
 * complained quantity per ZK/ZD line. The corrected invoice lines themselves
 * are edited in the invoice editor (quantity from the invoice minus the
 * complained quantity); the backend validates both at approval.
 */
export default function InvoiceComplaintEditor({ direction, invoice, lines, editable, locked = false, onSaved }: InvoiceComplaintEditorProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const feedback = useFeedback();
  const savedReason: Reason = invoice.correctionReason === 'COMPLAINT' ? 'COMPLAINT' : 'ORDINARY';
  const savedQuantities = useMemo(() => new Map((invoice.complaintLines ?? []).map((row) => [row.sourceLineId, row.quantity])),
    [invoice.complaintLines]);
  const [reason, setReason] = useState<Reason>(savedReason);
  const [quantities, setQuantities] = useState<Map<string, number>>(savedQuantities);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setReason(savedReason);
    setQuantities(savedQuantities);
  }, [savedReason, savedQuantities, invoice.version]);

  const complaintLines = Array.from(quantities.entries())
    .filter(([, quantity]) => quantity > 0)
    .map(([sourceLineId, quantity]) => ({ sourceLineId, quantity }));
  const hint = t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'complaintHintPurchase' : 'complaintHintSales'}`);

  if (!editable || invoice.status !== 'DRAFT') {
    if (savedReason !== 'COMPLAINT') return null;
    return <Alert type="info" showIcon data-testid="invoice-complaint-summary"
      message={t('invoices:workflow.corrections.complaintSummary', { lines: formatComplaintLines(invoice.complaintLines ?? [], lines) })}
      description={hint} />;
  }

  const save = async () => {
    if (!invoice.id || invoice.version == null) return;
    setBusy(true);
    try {
      await saveInvoiceComplaint(direction, invoice.id, {
        expectedVersion: invoice.version,
        reason: reason === 'COMPLAINT' ? 'COMPLAINT' : null,
        lines: reason === 'COMPLAINT' ? complaintLines : [],
      });
      feedback.success(t('invoices:workflow.corrections.complaintSaved'));
      onSaved();
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.corrections.complaintSaveFailed'));
    } finally {
      setBusy(false);
    }
  };
  const missingLines = reason === 'COMPLAINT' && complaintLines.length === 0;

  return <Space direction="vertical" size={8} style={{ width: '100%' }} data-testid="invoice-complaint-editor">
    <Typography.Text strong>{t('invoices:workflow.corrections.reasonLabel')}</Typography.Text>
    <Radio.Group value={reason} disabled={busy} onChange={(event) => setReason(event.target.value as Reason)}
      data-testid="invoice-correction-reason">
      <Radio value="ORDINARY">{t('invoices:workflow.corrections.reasonOrdinary')}</Radio>
      <Radio value="COMPLAINT" data-testid="invoice-correction-reason-complaint">{t('invoices:workflow.corrections.reasonComplaint')}</Radio>
    </Radio.Group>
    {reason === 'COMPLAINT' && <>
      <Typography.Text type="secondary">{hint}</Typography.Text>
      {lines == null
        ? <Alert type="warning" showIcon message={t('invoices:workflow.corrections.complaintLinesUnavailable')} />
        : <Table<InvoiceCoverageLine>
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={lines}
          columns={[
            { title: t('invoices:workflow.corrections.complaintLine'), render: (_, line) => <ProductNameWithDimensions name={line.name || '—'} dimensions={line} dimensionsTestId={`invoice-complaint-line-dimensions-${line.id}`} /> },
            { title: t('invoices:workflow.coverage.ordered'), align: 'right', render: (_, line) => formatCoverageQuantity(line.orderedQuantity, line.unit) },
            {
              title: t('invoices:workflow.corrections.complaintQuantity'),
              align: 'right',
              render: (_, line) => <InputNumber
                min={0}
                // Polish decimals („2,5”), no forced trailing zeros (antd would
                // take the precision from a fractional step); up to 3 decimals
                // are kept by roundWarehouseQuantity below.
                step={1}
                addonAfter={formatUnit(line.unit)}
                {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                disabled={busy}
                value={quantities.get(line.id) ?? null}
                aria-label={`${t('invoices:workflow.corrections.complaintQuantity')} ${line.name || ''}`}
                data-testid={`invoice-complaint-quantity-${line.id}`}
                onChange={(value) => setQuantities((current) => {
                  const next = new Map(current);
                  if (value == null || Number(value) <= 0) next.delete(line.id);
                  else next.set(line.id, roundWarehouseQuantity(value));
                  return next;
                })}
              />,
            },
          ]}
        />}
      {missingLines && <Typography.Text type="warning">{t('invoices:workflow.corrections.complaintNoLines')}</Typography.Text>}
    </>}
    {locked && <Typography.Text type="warning">{t('invoices:workflow.corrections.complaintSaveDirty')}</Typography.Text>}
    <Button onClick={() => void save()} loading={busy} disabled={locked || missingLines} data-testid="invoice-complaint-save">
      {t('invoices:workflow.corrections.complaintSave')}
    </Button>
  </Space>;
}
